// ── CreateKostenartModal ──────────────────────────────────────────────────────
// Small creation form: name + kind + optional parent (roots only, of same kind).
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Button, TextInput, Dropdown } from '../../../../shared/ui'
import type { Kostenart, CreateKostenartPayload } from '../api/kostenartenApi'

interface Props {
  open: boolean
  onClose: () => void
  roots: Kostenart[]
  onSave: (payload: CreateKostenartPayload) => void
  isSaving: boolean
}

export function CreateKostenartModal({ open, onClose, roots, onSave, isSaving }: Props) {
  const { t } = useTranslation('kostenarten')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'cost' | 'revenue'>('cost')
  const [parentId, setParentId] = useState<number | null>(null)
  const [nameError, setNameError] = useState<string | undefined>()

  // Roots available as parents — only same kind
  const parentOptions: Array<{ value: string; label: string }> = [
    { value: '', label: t('createModal.parentNone') },
    ...roots
      .filter(r => r.kind === kind)
      .map(r => ({ value: String(r.id), label: r.name })),
  ]

  function handleKindChange(v: string) {
    setKind(v as 'cost' | 'revenue')
    setParentId(null) // reset parent when kind changes
  }

  function handleSave() {
    const trimmed = name.trim()
    if (!trimmed) {
      setNameError('Bitte einen Namen eingeben')
      return
    }
    setNameError(undefined)
    onSave({ name: trimmed, kind, parentId })
  }

  function handleClose() {
    setName('')
    setKind('cost')
    setParentId(null)
    setNameError(undefined)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('createModal.title')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            {t('createModal.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} loading={isSaving}>
            {t('createModal.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextInput
          label={t('createModal.name')}
          required
          placeholder={t('createModal.namePlaceholder')}
          value={name}
          onChange={e => setName(e.target.value)}
          error={nameError}
          variant="glass"
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          autoFocus
        />
        <Dropdown
          label={t('createModal.kind')}
          value={kind}
          options={[
            { value: 'cost',    label: t('kind.cost') },
            { value: 'revenue', label: t('kind.revenue') },
          ]}
          onChange={v => handleKindChange(v)}
          variant="glass"
          usePortal
          menuZIndex={100010}
        />
        <Dropdown
          label={t('createModal.parent')}
          value={parentId ? String(parentId) : ''}
          options={parentOptions}
          onChange={v => setParentId(v ? Number(v) : null)}
          placeholder={t('createModal.parentNone')}
          variant="glass"
          usePortal
          menuZIndex={100010}
        />
      </div>
    </Modal>
  )
}
